import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Zap } from 'lucide-react';
import { Card } from '../components/ui/card';

const ConciergePage = () => {
  const { currentUser } = useAuth();
  const { resources, experts, addChatSession } = useData();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const navigate = useNavigate();

  const getMockResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    if (msg.includes('bess') || msg.includes('battery') || msg.includes('storage') || msg.includes('procure')) {
      return {
        text: "Here's a pathway built for procuring grid-scale storage. I'd start by sizing the system, then move to a bankable tender, and connect you with two people who've done this:",
        sources: [
          { id: 'res-1', type: 'TOOL', title: 'BESS Savings & Sizing Calculator', subtitle: 'Tool · do this first', icon: '⚡', bg: 'hsl(var(--accent-soft))' },
          { id: 'res-3', type: 'RESOURCE', title: 'Model Tender — Utility-Scale Storage', subtitle: 'Procurement · editable RFP', icon: '📄', bg: 'hsl(var(--blue-soft))' },
          { id: 'expert-1', type: 'EXPERT', title: 'Lerato Mokoena', subtitle: 'BESS expert · Eskom', icon: 'LM', bg: 'hsl(160 81% 17%)' }
        ],
        actions: [
          { label: 'Save as pathway', action: () => navigate('/me') },
          { label: 'View all BESS resources', action: () => navigate('/knowledge') }
        ]
      };
    }
    
    if (msg.includes('grid code') || msg.includes('regulation') || msg.includes('policy')) {
      return {
        text: "I can help with grid code reform. Here are the key resources and experts for your region:",
        sources: [
          { id: 'res-2', type: 'RESOURCE', title: 'Grid Code Reform: A Regulator\'s Playbook', subtitle: 'Policy · 12 markets benchmarked', icon: '📄', bg: 'hsl(var(--blue-soft))' }
        ],
        actions: [
          { label: 'Connect with peer regulators', action: () => navigate('/community') }
        ]
      };
    }
    
    if (msg.includes('digitiz') || msg.includes('scada') || msg.includes('adms')) {
      return {
        text: "For utility digitization, I recommend starting with the fundamentals course and then using our readiness assessment:",
        sources: [
          { id: 'course-2', type: 'COURSE', title: 'Utility Digitisation Pathways', subtitle: 'Course · 8 modules', icon: '🎓', bg: 'hsl(var(--violet-soft))' },
          { id: 'res-6', type: 'TOOL', title: 'Grid Digitization Readiness Assessment', subtitle: 'Tool · self-assessment', icon: '⚡', bg: 'hsl(var(--accent-soft))' }
        ],
        actions: []
      };
    }

    return {
      text: "I can help you find resources, courses, and experts across grid modernization topics. Try asking about: battery storage procurement, grid code reform, utility digitization, mini-grids, or connecting with specific experts.",
      sources: [],
      actions: [
        { label: 'Browse all resources', action: () => navigate('/knowledge') },
        { label: 'View courses', action: () => navigate('/academy') }
      ]
    };
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMsg = {
      id: Date.now(),
      role: 'user',
      text: input
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // Mock AI response with delay
    setTimeout(() => {
      const response = getMockResponse(input);
      const aiMsg = {
        id: Date.now() + 1,
        role: 'ai',
        ...response
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1000);
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>
              ✦ Grid Guide
            </span>
            <h2>The connective tissue of the platform</h2>
            <p className="mt-2" style={{ color: 'hsl(var(--ink-muted))' }}>
              One assistant that reads across the Repository, Academy and Community — and turns a question into a pathway.
            </p>
          </div>

          {/* Chat Area */}
          <div className="bg-white rounded-2xl shadow-sm border" style={{ borderColor: 'hsl(var(--line))', minHeight: '500px' }}>
            <div className="p-6 space-y-6" style={{ minHeight: '450px' }}>
              {messages.length === 0 && (
                <div className="flex items-center justify-center h-full py-20">
                  <div className="text-center max-w-md">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(var(--primary-soft))' }}>
                      <Zap className="w-8 h-8" style={{ color: 'hsl(var(--primary))' }} />
                    </div>
                    <h3 className="mb-2">How can I help you today?</h3>
                    <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Ask me about grid modernization, find resources, or connect with experts.</p>
                  </div>
                </div>
              )}

              {messages.map((message) => (
                <div key={message.id} className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : ''}`}>
                  {message.role === 'ai' && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white' }}>
                      <Zap className="w-4 h-4" fill="currentColor" />
                    </div>
                  )}
                  
                  <div className={`max-w-2xl ${message.role === 'user' ? 'order-first' : ''}`}>
                    {message.role === 'user' ? (
                      <div className="px-4 py-3 rounded-2xl inline-block" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white' }}>
                        {message.text}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-sm leading-relaxed">
                          {message.text}
                        </div>
                        
                        {message.sources && message.sources.length > 0 && (
                          <div className="space-y-2">
                            {message.sources.map((source) => (
                              <Card
                                key={source.id}
                                className="p-4 cursor-pointer hover:shadow-md flex items-center gap-3"
                                onClick={() => {
                                  if (source.type === 'EXPERT') navigate('/community');
                                  else if (source.type === 'COURSE') navigate('/academy');
                                  else navigate(`/knowledge/${source.id}`);
                                }}
                              >
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0" style={{ backgroundColor: source.bg, color: source.type === 'EXPERT' ? 'white' : 'inherit' }}>
                                  <span className="font-semibold">{source.icon}</span>
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{source.title}</div>
                                  <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>{source.subtitle}</div>
                                </div>
                                <div style={{ color: 'hsl(var(--ink-muted))' }}>→</div>
                              </Card>
                            ))}
                          </div>
                        )}
                        
                        {message.actions && message.actions.length > 0 && (
                          <div className="flex gap-2">
                            {message.actions.map((action, idx) => (
                              <Button key={idx} size="sm" variant="outline" onClick={action.action}>
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {message.role === 'user' && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: 'hsl(var(--blue))', color: 'white' }}>
                      {getInitials(currentUser?.name || 'U')}
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="flex gap-4">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))', color: 'white' }}>
                    <Zap className="w-4 h-4" fill="currentColor" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--ink-muted))', animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--ink-muted))', animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--ink-muted))', animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t" style={{ borderColor: 'hsl(var(--line))' }}>
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Ask anything about grid modernization..."
                  className="flex-1"
                />
                <Button onClick={handleSend} disabled={!input.trim() || isTyping}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConciergePage;
